import * as React from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faAngleDown, faAngleUp} from '@fortawesome/pro-solid-svg-icons';

import {AssortmentItem, CardItem} from '../item.class';
import {BlockHeader} from '../../ui/BlockHeader/BlockHeader';
import {ChangeEvent, useEffect, useState} from 'react';
import {CheckBox} from '../../ui/forms/CheckBox/CheckBox';
import {GaItemListName} from '../../../client/ga/ga-ecommerce.functions';
import {ItemCards} from '../ItemCard/ItemCards';
import {ItemToAdd} from '../../order-items/order-items.class';
import {ListsWorkflow} from '../../../client/lists/lists.workflow';
import {OrderItemsWorkflow} from '../../../client/order-items/order-items.workflow';
import {UpdateListItem} from '../../lists/list.class';
import {UserStateService} from '../../../client/users/user-state.service';
import {User} from '../../users/user.class';
import {useService} from '../../react/ServiceContext';
import {PageHeader} from '../../ui/PageHeader/PageHeader';
import Button from '../../ui/Buttons/Button';

const itemsToShow = 5;

type AssortmentContainsProps = {
    assortmentItems: AssortmentItem[];
    user: User;
};
export const AssortmentContains = ({assortmentItems, user}: AssortmentContainsProps) => {
    const componentName = `AssortmentContains`;
    const gaItemListName: GaItemListName = `assortment_contains`;
    const listsWorkflow: ListsWorkflow = useService(`listsWorkflow`);
    const orderItemsWorkflow: OrderItemsWorkflow = useService(`orderItemsWorkflow`);
    const userStateService: UserStateService = useService(`userStateService`);

    // Process queued actions
    useEffect(() => {
        if (userStateService.hasPendingAddToOrder(componentName)) {
            orderItemsWorkflow.addToOrderModal(userStateService.addToOrderItems, componentName, true);
        } else if (userStateService.hasPendingAddToList(componentName)) {
            listsWorkflow.addToList(
                userStateService.addToListAction,
                userStateService.addToListItems,
                componentName,
                gaItemListName,
                userStateService.addToListItemDesc,
                userStateService.addToListItemImage,
            );
        }
        // Only run once per page load
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const convertToCardItem = (): CardItem[] => {
        return assortmentItems.map((assortmentItem, index) => {
            return {
                ...assortmentItem,
                description: assortmentItem.name,
                item: assortmentItem.id,
                pkgQty: assortmentItem.reorderQty,
                unitsOrdered: 1,
                hidden: index >= itemsToShow,
            };
        });
    };

    const [assortmentItemsState, setAssortmentItemsState] = useState<CardItem[]>(convertToCardItem());
    const [operationsState, setOperationsEnabledState] = useState(false);
    const [seeAll, setSeeAll] = useState(false);

    /**
     * Add selected to order
     */
    const addSelectedToOrder = () => {
        const itemsToAdd: ItemToAdd[] = [];
        assortmentItemsState.forEach((assortmentItem) => {
            if (assortmentItem.checked) {
                itemsToAdd.push({
                    gaEcommerce: {item_list_name: gaItemListName},
                    item: assortmentItem.item,
                    unitsOrdered: assortmentItem.unitsOrdered,
                });
            }
        });
        orderItemsWorkflow.addToOrderModal(itemsToAdd, componentName, true);
    };

    /**
     * Add all to order
     */
    const addAllToOrder = () => {
        const itemsToAdd: ItemToAdd[] = [];
        assortmentItemsState.forEach((assortmentItem) => {
            itemsToAdd.push({
                gaEcommerce: {item_list_name: gaItemListName},
                item: assortmentItem.item,
                unitsOrdered: assortmentItem.unitsOrdered,
            });
        });
        orderItemsWorkflow.addToOrderModal(itemsToAdd, componentName, true);
    };

    /**
     * Add selected to List
     */
    const addSelectedToList = () => {
        const itemsToAdd: UpdateListItem[] = [];
        assortmentItemsState.forEach((assortmentItem) => {
            if (assortmentItem.checked) {
                itemsToAdd.push({item: assortmentItem.item, quantity: assortmentItem.unitsOrdered});
            }
        });
        listsWorkflow.addToList(`add`, itemsToAdd, componentName, gaItemListName, assortmentItems[0].name, assortmentItems[0].imagePath);
    };

    /**
     * Evaluates if assortmentItemsState contains checked items and enables list operations
     */
    const evaluateOperationsState = (cardItems: CardItem[]) => {
        let listOperationsEnabled = false;
        for (const productListItem of cardItems) {
            if (productListItem.checked) {
                listOperationsEnabled = true;
            }
        }
        setOperationsEnabledState(listOperationsEnabled);
    };

    /**
     * Records item quantity
     * @param itemNum
     * @param unitsOrdered
     */
    const onQtyUpdate = (itemNum: string, unitsOrdered: number) => {
        const updatedAssortmentItems = assortmentItemsState.map((listItems) => {
            if (itemNum === listItems.item) {
                return {...listItems, unitsOrdered, checked: true};
            }
            return listItems;
        });
        setAssortmentItemsState(updatedAssortmentItems);
        evaluateOperationsState(updatedAssortmentItems);
    };

    /**
     * Records item being checked
     * @param id - Item to evaluate
     * @param checked - Checked state
     */
    const recordItemCheckbox = (id: string, checked: boolean) => {
        const updatedAssortmentItems = assortmentItemsState.map((listItems) => {
            if (id === listItems.item) {
                return {...listItems, checked};
            }
            return listItems;
        });
        setAssortmentItemsState(updatedAssortmentItems);
        evaluateOperationsState(updatedAssortmentItems);
    };

    /**
     * Records item being checked
     * @param checked - Checked state
     */
    const selectAllCheckboxes = (checked: boolean) => {
        const updatedAssortmentItems = assortmentItemsState.map((listItems) => {
            return {...listItems, checked, hidden: false};
        });
        setAssortmentItemsState(updatedAssortmentItems);
        evaluateOperationsState(updatedAssortmentItems);
        setSeeAll(true);
    };

    /**
     * Set overflow items to hidden
     * @param hide - Checked state
     */
    const showOrHideOverflow = (hide: boolean) => {
        const updatedAssortmentItems = assortmentItemsState.map((listItems, index) => {
            return {...listItems, hidden: hide ? index >= itemsToShow : false};
        });
        setAssortmentItemsState(updatedAssortmentItems);
        evaluateOperationsState(updatedAssortmentItems);
    };

    /**
     * Template
     */
    return (
        <>
            <PageHeader
                title="Assortment Details"
                type="h4"
            >
                <span className="tw-text-base">{assortmentItems.length} Items</span>
            </PageHeader>
            <div>
                <BlockHeader className="!tw-mb-4">
                    <div className="tw-flex tw-justify-between tw-items-center tw-w-full">
                        <CheckBox
                            alias="ACselectAll"
                            className="!tw-hidden lg:!tw-inline-block"
                            label="Select All"
                            name="ACselectAll"
                            onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                const checked = event.target.checked;
                                if (checked) {
                                    setSeeAll(true);
                                }
                                selectAllCheckboxes(checked);
                            }}
                        />
                        <div className="tw-flex-grow tw-text-right">
                            <Button
                                className={`!tw-hidden lg:!tw-inline-block tw-mr-4${!operationsState ? ' tw-text-gray-600' : ''}`}
                                e2e="addToListWorkflowLinkAD"
                                disabled={!operationsState}
                                onClick={() => {
                                    addSelectedToList();
                                }}
                                size="sm"
                                variant="link"
                            >
                                Add to List
                            </Button>
                            <Button
                                className="tw-mt-0 !tw-hidden lg:!tw-inline-block"
                                e2e="addToOrderButtonAD"
                                disabled={!operationsState}
                                onClick={() => {
                                    addSelectedToOrder();
                                }}
                                size="sm"
                                variant="primary"
                            >
                                Add to Order
                            </Button>
                            <Button
                                block
                                className="tw-mt-0 lg:!tw-hidden"
                                onClick={() => {
                                    addAllToOrder();
                                }}
                                size="sm"
                                variant="primary"
                            >
                                Add All Items to Order
                            </Button>
                        </div>
                    </div>
                </BlockHeader>
            </div>
            <ItemCards
                cardOptions={{
                    addToList: true,
                    canEdit: false,
                    categoryLink: true,
                    contextMenu: true,
                    hideCheckboxMobile: true,
                    inventoryControls: false,
                }}
                componentName={componentName}
                gaItemListName={gaItemListName}
                items={assortmentItemsState}
                onStepperUpdate={onQtyUpdate}
                onStepperUpdateAllowInvalid={true}
                recordItemCheckbox={recordItemCheckbox}
                user={user}
            />
            <div className={`tw-w-full tw-text-right${seeAll ? '' : ' !tw-hidden'}`}>
                <Button
                    className="!tw-hidden lg:!tw-inline-block"
                    e2e="addToListWorkflowLinkAD"
                    disabled={!operationsState}
                    onClick={() => {
                        addSelectedToList();
                    }}
                    variant="link"
                >
                    Add to List
                </Button>
                <Button
                    className="tw-mt-0 !tw-hidden lg:!tw-inline-block"
                    e2e="addToOrderButtonAD"
                    disabled={!operationsState}
                    onClick={() => {
                        addSelectedToOrder();
                    }}
                    variant="primary"
                >
                    Add to Order
                </Button>
            </div>
            <div className="tw-text-center">
                <Button
                    onClick={() => {
                        showOrHideOverflow(seeAll);
                        setSeeAll(!seeAll);
                    }}
                    size="sm"
                    variant="link"
                >
                    {seeAll ? `See Less ` : `See More `}
                    <FontAwesomeIcon
                        icon={seeAll ? faAngleUp : faAngleDown}
                        suppressHydrationWarning
                    />
                </Button>
            </div>
        </>
    );
};
